import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Upload, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

export default function AdminContentSyncPage() {
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/content/export`);
      
      if (!response.ok) throw new Error('Export failed');
      
      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.headers.get('Content-Disposition')?.split('filename=')[1] || 'content_export.json';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Content exported successfully!');
    } catch (error) {
      toast.error('Export failed: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Confirmation dialog
    const confirmed = window.confirm(
      '⚠️ IMPORTANT: This will ADD or UPDATE content from the import file.\n\n' +
      '• Existing content with matching IDs will be UPDATED\n' +
      '• New content will be ADDED\n' +
      '• Content not in the import file will NOT be deleted\n\n' +
      'Are you sure you want to continue?'
    );
    
    if (!confirmed) {
      event.target.value = ''; // Reset file input
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${backendUrl}/api/admin/content/import`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Import failed');
      }

      setImportResult(result);
      const totalChanges = (result.total_new || 0) + (result.total_updated || 0);
      toast.success(`Import complete! ${result.total_new || 0} new, ${result.total_updated || 0} updated`);
    } catch (error) {
      toast.error('Import failed: ' + error.message);
      setImportResult({ error: error.message });
    } finally {
      setImporting(false);
      event.target.value = ''; // Reset file input
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bk-bg))] p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/admin/home')}>
            <ArrowLeft size={16} />
          </Button>
          <div>
            <h1 className="font-display text-3xl text-[hsl(var(--bk-fg))]">Content Sync</h1>
            <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] mt-1">
              Export content from Preview → Import to Production
            </p>
          </div>
        </div>

        {/* Warning Banner */}
        <Alert className="mb-6 border-yellow-500/50 bg-yellow-500/10">
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
          <AlertDescription className="text-[hsl(var(--bk-fg))] ml-2">
            <strong>Important:</strong> This tool syncs content between preview and production environments.
            Always export from preview, test changes, then import to production.
          </AlertDescription>
        </Alert>

        {/* Workflow Guide */}
        <Card className="p-6 mb-6 bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))]">
          <h2 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-4">Recommended Workflow</h2>
          <ol className="space-y-3 font-body text-sm text-[hsl(var(--bk-fg-muted))]">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--bk-accent))]/20 text-[hsl(var(--bk-accent))] flex items-center justify-center text-xs font-bold">1</span>
              <span>Edit content in <strong className="text-[hsl(var(--bk-fg))]">Preview Admin</strong> (paco-films.preview.emergentagent.com/admin)</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--bk-accent))]/20 text-[hsl(var(--bk-accent))] flex items-center justify-center text-xs font-bold">2</span>
              <span>Test changes on Preview site to verify everything looks correct</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--bk-accent))]/20 text-[hsl(var(--bk-accent))] flex items-center justify-center text-xs font-bold">3</span>
              <span>Click <strong className="text-[hsl(var(--bk-fg))]">"Export Content"</strong> below to download a JSON file</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--bk-accent))]/20 text-[hsl(var(--bk-accent))] flex items-center justify-center text-xs font-bold">4</span>
              <span>Go to <strong className="text-[hsl(var(--bk-fg))]">Production Admin</strong> (baklight.com/admin)</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--bk-accent))]/20 text-[hsl(var(--bk-accent))] flex items-center justify-center text-xs font-bold">5</span>
              <span>Click <strong className="text-[hsl(var(--bk-fg))]">"Import Content"</strong> and upload the JSON file</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[hsl(var(--bk-accent))]/20 text-[hsl(var(--bk-accent))] flex items-center justify-center text-xs font-bold">6</span>
              <span>Verify changes appear on Live site (baklight.com)</span>
            </li>
          </ol>
        </Card>

        {/* Export Section */}
        <Card className="p-6 mb-6 bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))]">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-2 flex items-center gap-2">
                <Download size={20} className="text-[hsl(var(--bk-accent))]" />
                Export Content
              </h3>
              <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] mb-4">
                Download all CMS content from this environment as a JSON file.
              </p>
              <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))] mb-4">
                Includes: Journal articles, projects, categories, testimonials, clients, success stories, and Mexico gallery.
              </p>
              <Button
                onClick={handleExport}
                disabled={exporting}
                className="bg-[hsl(var(--bk-accent))] text-white hover:opacity-90"
              >
                {exporting ? (
                  <>Exporting...</>
                ) : (
                  <>
                    <Download size={16} className="mr-2" />
                    Export All Content
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Import Section */}
        <Card className="p-6 bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))]">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-2 flex items-center gap-2">
                <Upload size={20} className="text-[hsl(var(--bk-accent))]" />
                Import Content
              </h3>
              <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] mb-4">
                Upload a previously exported JSON file to add or update content in this environment.
              </p>
              
              <Alert className="mb-4 border-blue-500/50 bg-blue-500/10">
                <AlertTriangle className="h-4 w-4 text-blue-500" />
                <AlertDescription className="text-[hsl(var(--bk-fg))] ml-2">
                  <strong>How it works:</strong> Content with matching IDs will be updated. New content will be added. Existing content NOT in the file will remain untouched.
                </AlertDescription>
              </Alert>

              <div className="relative">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  disabled={importing}
                  className="hidden"
                  id="import-file-input"
                  data-testid="import-file-input"
                />
                <label 
                  htmlFor="import-file-input"
                  className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-[hsl(var(--bk-accent))] text-white hover:opacity-90 h-10 px-4 py-2 cursor-pointer ${importing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  data-testid="import-button-label"
                >
                  {importing ? (
                    <>Importing...</>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      Import Content from File
                    </>
                  )}
                </label>
              </div>
            </div>
          </div>
        </Card>

        {/* Import Result */}
        {importResult && (
          <Card className="p-6 mt-6 bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))]">
            {importResult.error ? (
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <h4 className="font-display text-lg text-red-500 mb-2">Import Failed</h4>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))]">{importResult.error}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-green-500 flex-shrink-0 mt-1" size={20} />
                <div className="flex-1">
                  <h4 className="font-display text-lg text-green-500 mb-2">Import Successful!</h4>
                  <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] mb-4">
                    {importResult.total_new || 0} new documents added, {importResult.total_updated || 0} existing documents updated.
                  </p>
                  <div className="space-y-1">
                    {Object.entries(importResult.imported || {}).map(([collection, count]) => (
                      <p key={collection} className="font-mono-meta text-xs text-[hsl(var(--bk-fg-subtle))]">
                        {collection}: {count} new, {importResult.updated?.[collection] || 0} updated
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Technical Notes */}
        <Card className="p-6 mt-6 bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))]">
          <h3 className="font-display text-sm text-[hsl(var(--bk-fg))] mb-3">Technical Notes</h3>
          <ul className="space-y-2 font-body text-xs text-[hsl(var(--bk-fg-subtle))]">
            <li>• Exported files contain all CMS content but NOT uploaded images (images remain on Cloudflare R2)</li>
            <li>• Import ADDS new content and UPDATES existing content with matching IDs</li>
            <li>• Content not in the import file will NOT be deleted</li>
            <li>• Export files are timestamped for easy versioning</li>
            <li>• You can keep multiple export files as backups</li>
            <li>• Changes take effect immediately after import - no restart required</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
